import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Handshake, TrendingUp, Building2, User, Mail, Loader2, Globe, Megaphone, Settings, ExternalLink } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";

// Nav menu dark color for chart text
const NAV_DARK_COLOR = "#0F172A";

// Helper function to format week labels from "2025-W01" to "Jan 6"
const formatWeekLabel = (weekStr) => {
  if (!weekStr || !weekStr.includes("-W")) return weekStr;
  
  try {
    // Parse "2025-W01" format
    const [year, weekPart] = weekStr.split("-W");
    const weekNum = parseInt(weekPart, 10);
    
    // Calculate the date of the Monday of that week
    // Jan 4th is always in week 1, so we use that as reference
    const jan4 = new Date(parseInt(year, 10), 0, 4);
    const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);
    
    // Add weeks to get to the target week
    const targetDate = new Date(mondayOfWeek1);
    targetDate.setDate(mondayOfWeek1.getDate() + (weekNum - 1) * 7);
    
    // Format as "Jan 6"
    return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) {
    return weekStr;
  }
};

const SQLsPage = () => {
  const { sessionToken, user } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskName, maskEmail, maskValue, maskNumber, maskSource } = usePresentationMode();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [stageMapping, setStageMapping] = useState({});
  const [configuredSqlStages, setConfiguredSqlStages] = useState([]);
  const [data, setData] = useState({
    total: 0,
    sqls: [],
    trend: []
  });

  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== SQLsPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  // Check if current user is an admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || 
    user?.email?.endsWith('@jci-marketing.com') || 
    ['jason@jci-marketing.com', 'kevin@jci-marketing.com'].includes(user?.email);

  const handleDateChange = useCallback((dateInfo) => {
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  // Fetch stage mapping for label translation
  const fetchStageMapping = async () => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/integrations/hubspot/lifecycle-stages`, { params });
      const mapping = response.data?.stage_mapping || {};
      setStageMapping(mapping);
    } catch (error) {
      console.error("Error fetching stage mapping:", error);
    }
  };

  // Get stage label from ID
  const getStageLabel = (stageId) => {
    if (!stageId) return "SQL";
    // Check if we have a mapping for this ID
    if (stageMapping[stageId]) return stageMapping[stageId];
    // Check if it's already a label (not a number)
    if (isNaN(stageId)) return stageId;
    // Return the ID if no mapping found
    return stageId;
  };

  const fetchSQLs = async () => {
    setLoading(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      if (selectedRegion && selectedRegion !== "all") params.region = selectedRegion;
      if (selectedSource && selectedSource !== "all") params.source = selectedSource;

      console.log("=== SQLsPage fetchSQLs ===", "tenantId:", viewingTenantId);
      const response = await axios.get(`${API}/analytics/sqls`, { params });
      setData(response.data);
      
      // Update stage mapping if backend provides one (ID -> label)
      if (response.data.stage_mapping) {
        setStageMapping(response.data.stage_mapping);
      }
    } catch (error) {
      console.error("Error fetching SQLs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch configured SQL stages from tenant settings
  const fetchConfiguredStages = async () => {
    const viewingTenantId = currentTenantId || sessionStorage.getItem("viewing_tenant_id");
    // Don't fetch if no tenant ID is available
    if (!viewingTenantId) {
      console.log("No tenant ID available yet, skipping stage fetch");
      return;
    }
    
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/tenants/my-tenant-settings`, { params });
      
      const stages = response.data?.hubspot_stage_mapping?.sql_stages || [];
      console.log("Fetched SQL stages from tenant settings:", stages, "for tenant:", viewingTenantId);
      setConfiguredSqlStages(stages);
    } catch (error) {
      console.error("Error fetching configured stages:", error);
    }
  };

  useEffect(() => {
    if (sessionToken && currentTenantId) {
      fetchStageMapping();
      fetchConfiguredStages();
    }
  }, [sessionToken, currentTenantId]); // CRITICAL: Re-fetch when tenant changes
  
  // Also listen for tenant-changed event to re-fetch stages immediately
  useEffect(() => {
    const handleTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId) {
        console.log("tenant-changed event received, re-fetching stages for:", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    window.addEventListener("tenant-changed", handleTenantChange);
    return () => window.removeEventListener("tenant-changed", handleTenantChange);
  }, []);

  useEffect(() => {
    if (!dateFilterInitialized) return;
    // Fetch data from local database - NO sync on page load
    fetchSQLs();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, selectedRegion, selectedSource, currentTenantId]); // CRITICAL: Added currentTenantId

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="sqls-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Handshake className="w-6 h-6 text-green-600" />
            {pageTitles?.sqls || "Sales Qualified Leads"}
          </h1>
          <p className="text-slate-600 mt-1">
            Leads ready for sales engagement
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger className="w-40">
              <Megaphone className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="PAID_SEARCH">Paid Search</SelectItem>
              <SelectItem value="ORGANIC_SEARCH">Organic</SelectItem>
              <SelectItem value="DIRECT_TRAFFIC">Direct</SelectItem>
              <SelectItem value="REFERRALS">Referral</SelectItem>
              <SelectItem value="SOCIAL_MEDIA">Social</SelectItem>
              <SelectItem value="EMAIL_MARKETING">Email</SelectItem>
              <SelectItem value="PAID_SOCIAL">Paid Social</SelectItem>
              <SelectItem value="OFFLINE">Offline</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-36">
              <Globe className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="na">NA</SelectItem>
              <SelectItem value="emea">EMEA</SelectItem>
              <SelectItem value="apac">APAC</SelectItem>
            </SelectContent>
          </Select>
          <PageDateFilter pageId="sqls" onDateChange={handleDateChange} />
        </div>
      </div>

      {/* Mapped Stages Indicator */}
      <Card 
        className={`border-green-200 bg-green-50/50 ${isAdmin ? 'cursor-pointer hover:bg-green-100 transition-colors' : ''}`}
        onClick={isAdmin ? () => navigate('/app/admin?tab=features') : undefined}
      >
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-4 h-4 text-green-600" />
              <div>
                <span className="text-sm font-medium text-green-800">Mapped Stages: </span>
                {configuredSqlStages.length > 0 ? (
                  <span className="text-sm text-green-700">
                    {configuredSqlStages.join(', ')}
                  </span>
                ) : (
                  <span className="text-sm text-green-500 italic">
                    Using defaults (SQL, salesqualifiedlead)
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <span>Edit in Settings</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="chart-container">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total SQLs</p>
                    <p className="text-3xl font-bold text-slate-900">{maskNumber(data.total)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Handshake className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="chart-container">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">This Period</p>
                    <p className="text-3xl font-bold text-slate-900">{maskNumber(data.sqls.length)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend Chart */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: NAV_DARK_COLOR }}>
                SQL Trend by Week {selectedRegion !== "all" && `(${selectedRegion.toUpperCase()})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart 
                    data={data.trend.map(t => ({ ...t, weekLabel: formatWeekLabel(t.week) }))} 
                    margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="weekLabel" tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} />
                    <YAxis 
                      tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} 
                      tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                    />
                    <Tooltip 
                      contentStyle={{ color: NAV_DARK_COLOR }} 
                      labelFormatter={(label) => `Week of ${label}`}
                      formatter={isPresentationMode ? (value) => [maskNumber(value), 'SQLs'] : undefined}
                    />
                    <Bar dataKey="count" fill={chartColors[0]} name="SQLs" radius={[4, 4, 0, 0]}>
                      {!isPresentationMode && (
                        <LabelList dataKey="count" position="top" fill={NAV_DARK_COLOR} fontSize={11} fontWeight={600} />
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No trend data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* SQL Table */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Recent SQLs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.sqls.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.sqls.slice(0, 20).map((sql, index) => (
                      <TableRow key={sql.hubspot_id || index}>
                        <TableCell className="font-medium">
                          {isPresentationMode 
                            ? maskName(`${sql.first_name || sql.firstname || ''} ${sql.last_name || sql.lastname || ''}`.trim(), index)
                            : `${sql.first_name || sql.firstname || ''} ${sql.last_name || sql.lastname || ''}`.trim() || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-slate-600">
                            <Mail className="w-3 h-3" />
                            {isPresentationMode ? maskEmail(sql.email) : (sql.email || "-")}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            {isPresentationMode ? maskValue(sql.company, 'tenant') : (sql.company || "-")}
                          </div>
                        </TableCell>
                        <TableCell>{isPresentationMode ? maskValue(sql.job_title || sql.jobtitle) : (sql.job_title || sql.jobtitle || "-")}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            {getStageLabel(sql.lifecycle_stage || sql.lifecyclestage)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(sql.created_at || sql.createdate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  No SQLs found for the selected period. Try adjusting the date filter or check your HubSpot stage mapping in Settings.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SQLsPage;
